/**
 * Currency Note API
 *
 * Fetches note definitions from the server and provides
 * denomination values, CDN texture URLs, and colors.
 */

import { serverConfig } from '../network/ServerConfig';

export interface CurrencyNoteData {
  noteId: string;
  value: number;
  denomination: string;
  denominationWord: string;
  hue: number;
  saturation: number;
  brightness: number;
  cdn: {
    large: string;
    medium: string;
    small: string;
  };
}

let cachedNotes: CurrencyNoteData[] | null = null;

/**
 * Fetch all currency notes from the server.
 * Caches the result — call once on startup.
 */
export async function fetchCurrencyNotes(): Promise<CurrencyNoteData[]> {
  if (cachedNotes) return cachedNotes;

  try {
    const resp = await fetch(`${serverConfig.mainServerUrl}/api/currency-notes`);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const notes: CurrencyNoteData[] = await resp.json();
    // Sort by value ascending
    notes.sort((a, b) => a.value - b.value);
    cachedNotes = notes;
    console.log(`💵 Loaded ${notes.length} currency notes from server`);
    return notes;
  } catch (err) {
    console.warn('⚠️ Failed to load currency notes from server, using fallback', err);
    return [];
  }
}

/**
 * Get the cached notes (must call fetchCurrencyNotes first).
 */
export function getCachedNotes(): CurrencyNoteData[] {
  return cachedNotes ?? [];
}

/**
 * Find the note definition for a given denomination value.
 * Returns the exact match, or the closest available note.
 */
export function getNoteForValue(value: number): CurrencyNoteData | null {
  const notes = getCachedNotes();
  if (notes.length === 0) return null;

  // Exact match
  const exact = notes.find(n => n.value === value);
  if (exact) return exact;

  // Closest match
  let closest = notes[0];
  let minDiff = Math.abs(value - closest.value);
  for (const note of notes) {
    const diff = Math.abs(value - note.value);
    if (diff < minDiff) {
      minDiff = diff;
      closest = note;
    }
  }
  return closest;
}

/**
 * Get the medium CDN texture URL for a denomination value.
 * Used by Bill.ts for bill textures.
 */
export function getNoteCdnUrl(value: number, size: 'large' | 'medium' | 'small' = 'medium'): string | null {
  const note = getNoteForValue(value);
  return note?.cdn[size] ?? null;
}
